import { IconButton, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { ColumnDirectionAlignStartFlexFill, RowJustifyEndFlex, StyledTextField } from "../commons/Containers";
import _ from "lodash";
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { ButtonType } from "./ButtonFactory";
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import DeleteIcon from '@mui/icons-material/Delete';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";

const NonValueButtonTypes = ['quick_reply'];
const ExampleValueButtonTypes = ['url'];

export const ButtonsComponent = ({ component, componentHandler, editing }) => {

    const [addAnchor, setAddAnchor] = useState();
    const [buttons, setButtons] = useState([]);
    const columnHelper = createColumnHelper();
    const buttonTableColumns = useMemo(() => [
        columnHelper.display({ id: 'move', cell: ({ row }) => <ViewCompactIcon /> }),
        {
            accessorKey: 'type',
            header: 'Type',
            cell: ({ row }) => <ButtonType row={row} />
        },
        {
            accessorKey: 'text',
            header: 'Display Text',
            cell: ({ row }) => <TextField editing={editing} row={row} value={buttons.find(e => e.id === row.original.id)?.text} handleText={displayTextHandler} />
        },
        {
            accessorFn: (row) => getButtonValue(row),
            header: 'Value',
            cell: ({ row }) => NonValueButtonTypes.some(e => e === row.original.type) ? null : <TextField row={row} value={getButtonValue(row)} handleText={handleButtonValue} />,
        },
        {
            accessorFn: (row) => getExampleValue(row),
            header: 'Example',
            cell: ({ row }) => ExampleValueButtonTypes.some(e => e === row.original.type) && containsIndexedUrlParam(getButtonValue(row)) ? <TextField row={row} value={getUrlButtonExample(row)} handleText={handleUrlButtonExample} /> : null,
        },
        columnHelper.display({ id: 'action', cell: ({ row }) => <IconButton onClick={() => setButtons(prev => prev.filter(e => e.id !== row.original.id))}><DeleteIcon fontSize="small" /></IconButton> }),
    ], [buttons]);
    const table = useReactTable({ data: buttons, columns: buttonTableColumns, getCoreRowModel: getCoreRowModel() });
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (component && !_.isEmpty(component.buttons)) {
            setButtons(component.buttons);
        }
    }, [component]);

    useEffect(() => {
        if (buttons.length !== component.buttons?.length) {
            componentHandler({ ...component, buttons: buttons });
        }
    }, [buttons])

    const handleClose = () => {
        setAddAnchor(null);
    };

    const getButtonValue = (row) => {
        if (row.original.type === 'url') {
            return row.original.url;
        }
        if (row.original.type === 'phone_number') {
            return row.original.phoneNumber;
        }
        return '';
    };

    const getUrlButtonExample = (row) => {
        return row.original._example;
    };

    const containsIndexedUrlParam = (value = '') => {
        return value && value.endsWith('{{1}}');
    }

    const handleButtonValue = (row, value) => {
        if (row.original?.type === 'url') {
            return componentHandler({ ...component, buttons: buttons.map(e => e.id === row.original.id ? { ...e, url: value } : e) })
        }
        if (row.original?.type === 'phone_number') {
            return componentHandler({ ...component, buttons: buttons.map(e => e.id === row.original.id ? { ...e, phoneNumber: value } : e) })
        }
        return '';
    };

    const handleUrlButtonExample = (row, value) => {
        return componentHandler({ ...component, buttons: buttons.map(e => e.id === row.original.id ? { ...e, _example: value } : e) })
    };

    const getExampleValue = (row) => {
        return '';
    };

    const handleButtonAdd = ({ type }) => {
        const list = _.clone(buttons);
        list.push({ type: type, id: v4() });
        setButtons(list);
        handleClose();
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setButtons((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const displayTextHandler = (row, value) => {
        componentHandler({ ...component, buttons: buttons.map(e => e.id === row.original.id ? { ...e, text: value } : e) });
    };

    const TextField = ({ row, value, handleText, editing }) => {
        return <StyledTextField editing={editing} width={'15ch'} value={value ? value : ''} valueHandler={(value) => handleText(row, value)} />
    };

    return (
        <ColumnDirectionAlignStartFlexFill padding={1}>
            <RowJustifyEndFlex>
                <IconButton disabled={!editing} size="medium" onClick={(e) => setAddAnchor(e.currentTarget)}>
                    <AddIcon fontSize="medium" />
                </IconButton>
            </RowJustifyEndFlex>
            <Menu
                id="basic-menu"
                anchorEl={addAnchor}
                open={Boolean(addAnchor)}
                onClose={handleClose}
                slotProps={{
                    list: {
                        'aria-labelledby': 'basic-button',
                    },
                }}
            >
                <MenuItem onClick={() => handleButtonAdd({ type: 'phone_number' })}>Phone</MenuItem>
                <MenuItem onClick={() => handleButtonAdd({ type: 'url' })}>Website/Link</MenuItem>
                <MenuItem onClick={() => handleButtonAdd({ type: 'quick_reply' })}>Quick reply</MenuItem>
            </Menu>
            <DndContext sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}>
                <SortableContext items={buttons.map(e => e.id)} strategy={verticalListSortingStrategy}>
                    <TableContainer sx={{ minWidth: 0, overflowX: 'auto', overflowY: 'visible' }}>
                        <Table>
                            <TableHead>
                                {table.getHeaderGroups().map(headerGroup => <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map(header => <TableCell colSpan={header.colSpan} key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</TableCell>)}
                                </TableRow>)}
                            </TableHead>
                            <TableBody>
                                {table.getRowModel().rows.map(row => <TableRow key={row.id}>{row.getVisibleCells().map(cell => <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>)}</TableRow>)}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </SortableContext>
            </DndContext>
        </ColumnDirectionAlignStartFlexFill>
    );

};